import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Globe, Navigation, ExternalLink } from 'lucide-react';

export default function LocationTab({ formData, onInputChange }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-100">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-emerald-100 rounded-lg mr-4">
            <MapPin className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{t('settings.tabs.location')}</h3>
            <p className="text-sm text-gray-600">{t('settings.location.description')}</p>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">{t('settings.address')}</h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.addressDescription')}</p>
        </div>
        
        <div className="p-6">
          <textarea
            value={formData.address}
            onChange={(e) => onInputChange('address', e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
            placeholder={t('settings.addressPlaceholder')}
          />
          <div className="text-xs text-gray-500 mt-2">
            {formData.address?.length || 0} {t('settings.characters')}
          </div>
        </div>
      </div>

      {/* Coordinates */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">{t('settings.coordinates')}</h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.coordinatesDescription')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Navigation className="w-4 h-4 inline mr-1" />
                {t('settings.latitude')}
              </label>
              <input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => onInputChange('latitude', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                placeholder="31.9539"
              />
              <p className="text-xs text-gray-500 mt-1">{t('settings.latitudeRange')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Globe className="w-4 h-4 inline mr-1" />
                {t('settings.longitude')}
              </label>
              <input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => onInputChange('longitude', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                placeholder="35.9106"
              />
              <p className="text-xs text-gray-500 mt-1">{t('settings.longitudeRange')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Map Preview */}
      {formData.latitude && formData.longitude ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-emerald-50 px-6 py-4 border-b border-emerald-200">
            <h4 className="text-lg font-semibold text-emerald-900 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {t('settings.locationPreview')}
            </h4>
          </div>
          
          <div className="p-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-emerald-700 mb-2">
                <strong>{t('settings.coordinates')}:</strong> {formData.latitude}, {formData.longitude}
              </p>
              <p className="text-xs text-emerald-600">{t('settings.coordinatesNote')}</p>
            </div>
            
            <a
              href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('settings.viewOnMaps')}
            </a>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8 text-center">
            <MapPin className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">{t('settings.noLocationSet')}</h4>
            <p className="text-gray-600">{t('settings.addCoordinatesToSeePreview')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
